import * as React from "react";
import type {Theme, Components} from "../..";
import Zoom from "../../../components/Zoom";
import SvgIcon from "../../../components/SvgIcon";
import {styled, darken} from "../..";

type CheckboxOverrides = Components<Theme>["MuiCheckbox"];

/**
 * @typedef {undefined} CheckboxColor
 * @typedef {( "small" | "medium" | "large" )} CheckboxSize
 * @typedef {undefined} CheckboxIndeterminate
 * @typedef {undefined} CheckboxIndeterminateIcon
 */
declare module "@mui/material/Checkbox" {
    interface CheckboxPropsSizeOverrides {
        large: true;
    }

    interface CheckboxProps {
        /**
         * @param {CheckboxColor} [color] - The Next theme has no color variants for Checkbox.
         * Don't use this prop if you want the Next theme.
         */
        color?:
            | "primary"
            | "secondary"
            | "error"
            | "info"
            | "success"
            | "warning"
            | "default";
        /**
         * @param {CheckboxSize} [size] - The Next theme supports an extra size: "large"
         * It is not supported by the classic Toolkit theme
         */
        size?: "small" | "medium" | "large";
        /**
         * @param {CheckboxIndeterminate} [indeterminate] - The Next theme has no indeterminate state for Checkbox.
         * Don't use this prop if you want the Next theme.
         */
        indeterminate?: boolean;
        /**
         * @param {CheckboxIndeterminateIcon} [indeterminateIcon] - The Next theme has no indeterminate icon for Checkbox.
         * Don't use this prop if you want the Next theme.
         */
        indeterminateIcon?: React.ReactNode;
    }
}

const CheckboxBlankIcon = styled("span")(({theme}) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 16,
    height: 16,
    borderRadius: "50%",
    border: `1.5px solid ${theme.color.border.subtle}`,

    ".Mui-disabled &": {
        borderColor: theme.color.border.disabled,
        backgroundColor: theme.color.fill.neutral.subtle,
    },
}));

const CheckboxCheckedIcon = styled("span")(({theme}) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.color.text.inverted.default,
    width: 16,
    height: 16,
    borderRadius: "50%",
    backgroundColor: theme.color.border.interactive,

    ".Mui-disabled &": {
        backgroundColor: theme.color.fill.neutral.subtle,
        border: `1.5px solid ${theme.color.border.subtle}`,
        color: theme.color.border.default,
    },
}));

function DefaultIcon({checked = false}: {checked?: boolean}) {
    return (
        <>
            <CheckboxBlankIcon className="MuiCheckbox-blankIcon" />

            <Zoom in={checked} style={{transitionDuration: "150ms"}}>
                <CheckboxCheckedIcon
                    sx={{position: "absolute"}}
                    className="MuiCheckbox-checkedIcon"
                >
                    <SvgIcon>
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M19.497 6.379a1.5 1.5 0 0 1 .124 2.118l-7.863 8.846-.002.002a1.7 1.7 0 0 1-2.41.136l-.002-.002-4.848-4.364a1.5 1.5 0 1 1 2.008-2.23l3.878 3.491 6.997-7.872a1.5 1.5 0 0 1 2.117-.125Z"
                        />
                    </SvgIcon>
                </CheckboxCheckedIcon>
            </Zoom>
        </>
    );
}

const overrides: CheckboxOverrides = {
    defaultProps: {
        checkedIcon: <DefaultIcon checked />,
        icon: <DefaultIcon />,
        size: "medium",
    },
    styleOverrides: {
        root: ({theme}) => ({
            // padding: 4, actual padding in DS docs
            padding: 8, // aligns better with other components

            "&:not(.Mui-disabled):hover, .MuiFormControlLabel-root:hover &:not(.Mui-disabled)":
                {
                    backgroundColor: "unset",

                    "& .MuiCheckbox-blankIcon": {
                        borderColor: darken(theme.color.border.subtle, 0.3),
                        backgroundColor: theme.color.interaction.hovered,
                    },
                },

            "&.Mui-focusVisible .MuiCheckbox-blankIcon": {
                outline: `1.5px solid ${theme.color.border.focused}`,
                borderColor: theme.color.border.focused,
                outlineOffset: 2,
            },
        }),
    },
    variants: [
        {
            props: {size: "medium"},
            style: {
                "& .MuiCheckbox-blankIcon, & .MuiCheckbox-checkedIcon": {
                    width: 20,
                    height: 20,

                    "& .MuiSvgIcon-root": {
                        fontSize: 14,
                    },
                },
            },
        },
        {
            props: {size: "small"},
            style: {
                "& .MuiCheckbox-blankIcon, & .MuiCheckbox-checkedIcon": {
                    "& .MuiSvgIcon-root": {
                        fontSize: 11,
                    },
                },
            },
        },
    ],
};

export default overrides;
